import React, { useState, useMemo, useCallback } from 'react';
import ColumnFilterPopup from './ColumnFilterPopup';
import {
  fmtDateFull, getProdDays, compareOp, InlineEdit,
  TYPE_LABELS, fmtTimestamp, fmtDateShort,
} from './ShipmentsTable';

const COLLAPSE_STORAGE_KEY  = 'sp_wm_collapsed_types';
const PROD_PLAN_STORAGE_KEY = 'sp_wm_prod_plan_open';
const SHIPMENT_STORAGE_KEY  = 'sp_wm_shipment_open';
const WFS_STORAGE_KEY       = 'sp_wm_wfs_open';

const loadCollapsed = () => {
  try {
    const saved = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const saveCollapsed = (state) => {
  try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state)); } catch {}
};

const WalmartShipmentsTable = ({
  products, dailyPlan, producedData = {}, shipDate,
  boxInputs, setBoxInputs,
  wfsUpdatedAt,
  velocityData = {},
  actualSalesData = {},
  forecastSalesData = {},
  minBoxesFilter = {},
  onSaveBoxInputs,
  onClearBoxInputs,
  boxSaveStatus,
  commentsData = {},
  onSaveComment,
}) => {
  const prodDays = useMemo(() => getProdDays(shipDate), [shipDate]);

  const [collapsedTypes, setCollapsedTypes] = useState(loadCollapsed);

  const [prodPlanOpen, setProdPlanOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(PROD_PLAN_STORAGE_KEY);
      return saved === null ? true : JSON.parse(saved);
    } catch { return true; }
  });

  const [shipmentOpen, setShipmentOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(SHIPMENT_STORAGE_KEY);
      return saved === null ? true : JSON.parse(saved);
    } catch { return true; }
  });

  const [wfsOpen, setWfsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(WFS_STORAGE_KEY);
      return saved === null ? false : JSON.parse(saved);
    } catch { return false; }
  });

  // Sorting by Days Supplied columns (within each product group)
  const [daysSort, setDaysSort] = useState({ col: null, dir: 'asc' });

  // Per-column Days Supplied filters: { cur|inbound|ship: { operator, value } }
  const [daysFilters, setDaysFilters] = useState({});

  const toggleCollapse = useCallback((type) => {
    setCollapsedTypes(prev => {
      const next = { ...prev, [type]: !prev[type] };
      saveCollapsed(next);
      return next;
    });
  }, []);

  const toggleProdPlan = useCallback(() => {
    setProdPlanOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(PROD_PLAN_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleShipment = useCallback(() => {
    setShipmentOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(SHIPMENT_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const toggleWfs = useCallback(() => {
    setWfsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(WFS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const handleDaysSort = useCallback((col) => {
    setDaysSort(prev =>
      prev.col !== col ? { col, dir: 'asc' }
      : prev.dir === 'asc' ? { col, dir: 'desc' }
      : { col: null, dir: 'asc' }
    );
  }, []);

  const daysInCurrentMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }, []);

  const actualSalesMeta = useMemo(() => {
    let updatedAt = null, dateFrom = null, dateTo = null;
    Object.values(actualSalesData).forEach(row => {
      if (row.updated_at && (!updatedAt || row.updated_at > updatedAt)) {
        updatedAt = row.updated_at;
        dateFrom  = row.date_from;
        dateTo    = row.date_to;
      }
    });
    return { updatedAt, dateFrom, dateTo };
  }, [actualSalesData]);

  const velUpdatedAt = useMemo(() => {
    let ts7 = null, from7 = null, to7 = null;
    let ts30 = null, from30 = null, to30 = null;
    Object.values(velocityData).forEach(row => {
      if (row.vel_7d_updated_at && (!ts7 || row.vel_7d_updated_at > ts7)) {
        ts7 = row.vel_7d_updated_at;
        from7 = row.vel_7d_from;
        to7   = row.vel_7d_to;
      }
      if (row.vel_30d_updated_at && (!ts30 || row.vel_30d_updated_at > ts30)) {
        ts30 = row.vel_30d_updated_at;
        from30 = row.vel_30d_from;
        to30   = row.vel_30d_to;
      }
    });
    return { ts7, from7, to7, ts30, from30, to30 };
  }, [velocityData]);

  const planLookup = useMemo(() => {
    const map = {};
    dailyPlan.forEach(row => {
      if (!map[row.sku]) map[row.sku] = {};
      map[row.sku][row.date] = (map[row.sku][row.date] || 0) + row.qty_to_produce;
    });
    return map;
  }, [dailyPlan]);

  const handleBoxInput = (sku, val) => {
    const num = val === '' ? '' : Math.max(0, parseInt(val) || 0);
    setBoxInputs(prev => ({ ...prev, [sku]: num }));
  };

  // Days Supplied: cur = WFS Available only, inbound = Available + Inbound, ship = + planned units
  const calcDays = useCallback((p) => {
    const vel = velocityData[p.SKU]?.vel_7d != null ? parseFloat(velocityData[p.SKU].vel_7d) : null;
    if (vel == null || vel <= 0) return { cur: null, inbound: null, ship: null };
    const avail = Number(p.stock_wfs_available) || 0;
    const total = Number(p.stock_wfs) || 0;
    const boxInput = boxInputs[p.SKU];
    const shipUnits = boxInput !== undefined && boxInput !== '' ? Number(boxInput) * (Number(p.bagsPerBox) || 0) : 0;
    return {
      cur:     avail / vel,
      inbound: total / vel,
      ship:    (total + shipUnits) / vel,
    };
  }, [velocityData, boxInputs]);

  // Group products by type, applying minBoxesFilter { min, max } and days filters
  const grouped = useMemo(() => {
    const order = ['sets', 'wildmicr', 'individualpackets', 'fert_products', 'readyproducts', 'kit'];
    const groups = {};
    const minVal = minBoxesFilter?.min !== '' && minBoxesFilter?.min != null ? Number(minBoxesFilter.min) : null;
    const maxVal = minBoxesFilter?.max !== '' && minBoxesFilter?.max != null ? Number(minBoxesFilter.max) : null;
    const hasFilter = minVal !== null || maxVal !== null;
    const activeDaysFilters = Object.entries(daysFilters).filter(([, f]) => f && f.value !== '');

    products.forEach(p => {
      if (activeDaysFilters.length > 0) {
        const d = calcDays(p);
        const pass = activeDaysFilters.every(([col, f]) =>
          d[col] != null && compareOp(d[col], f.operator, parseFloat(f.value))
        );
        if (!pass) return;
      }
      if (hasFilter) {
        const sku = p.SKU;
        const bagsPerBox = Number(p.bagsPerBox) || 0;
        if (bagsPerBox > 0) {
          const stockWH = Number(p.stock_wh) || 0;
          const shipAddedPcs = Number(p.shipment_added_box) * bagsPerBox;
          const willBeTaken = shipAddedPcs - Number(p.shipment_needed_box) * bagsPerBox;
          const prodSum = prodDays.reduce((sum, d) => sum + (planLookup[sku]?.[d] || 0), 0);
          const producedSum = prodDays.reduce((sum, d) => sum + (producedData[sku]?.[d] || 0), 0);
          const remainingProd = Math.max(0, prodSum - producedSum);
          const boxesCanShip = Math.floor((stockWH + willBeTaken + remainingProd) / bagsPerBox);
          if (minVal !== null && boxesCanShip < minVal) return;
          if (maxVal !== null && boxesCanShip > maxVal) return;
        } else {
          if (minVal !== null && minVal > 0) return;
        }
      }
      if (!groups[p.type]) groups[p.type] = [];
      groups[p.type].push(p);
    });
    if (daysSort.col) {
      const dir = daysSort.dir === 'desc' ? -1 : 1;
      Object.values(groups).forEach(rows => rows.sort((a, b) => {
        const da = calcDays(a)[daysSort.col];
        const db = calcDays(b)[daysSort.col];
        if (da == null && db == null) return 0;
        if (da == null) return 1;
        if (db == null) return -1;
        return (da - db) * dir;
      }));
    }
    return order.filter(t => groups[t] && groups[t].length > 0).map(t => ({ type: t, rows: groups[t] }));
  }, [products, minBoxesFilter, prodDays, planLookup, producedData, daysSort, daysFilters, calcDays]);

  const STICKY_COLS = 5;
  const totalCols = STICKY_COLS + 1 + (wfsOpen ? 4 : 2) + 1 + (shipmentOpen ? 3 : 0) + 1
    + (prodPlanOpen ? prodDays.length : 0) + 10 + 1 + 3 + 1;

  // Totals for Boxes, Units and SKU types (across all visible rows)
  const { totalBoxes, totalUnits, totalSkuTypes } = useMemo(() => {
    let boxes = 0, units = 0, skuTypes = 0;
    grouped.forEach(({ rows }) => {
      rows.forEach(product => {
        const sku = product.SKU;
        const bagsPerBox = Number(product.bagsPerBox) || 0;
        const boxInput = boxInputs[sku];
        if (boxInput !== undefined && boxInput !== '') {
          const num = Number(boxInput);
          if (num > 0) {
            boxes += num;
            units += num * bagsPerBox;
            skuTypes += 1;
          }
        }
      });
    });
    return { totalBoxes: boxes, totalUnits: units, totalSkuTypes: skuTypes };
  }, [grouped, boxInputs]);

  return (
    <div className="sp-table-container">
      <table className="sp-table">
        <thead>
          {/* Row 1: Group headers */}
          <tr className="sp-thead-group">
            <th className="sp-th sp-th-sticky sp-th-sticky-1 sp-th-group" rowSpan={2}>#</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-2 sp-th-group" rowSpan={2}>Product Name</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-3 sp-th-group" rowSpan={2}>Brand</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-4 sp-th-group" rowSpan={2}>GTIN</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-5 sp-th-group" rowSpan={2}>SKU</th>
            <th className="sp-th sp-th-group sp-block-left" rowSpan={2} title="Out of Stock: WFS Available is 0 or SKU missing from the stock file">OOS</th>
            <th className="sp-th sp-th-group-header" colSpan={wfsOpen ? 4 : 2}>Stock</th>
            <th className="sp-th sp-th-group sp-th-reserved-boxes" rowSpan={2} title="Total quantity of all boxes not yet added to shipments">
              Reserved<br/>boxes
              <button
                className={`sp-prod-toggle${shipmentOpen ? '' : ' collapsed'}`}
                onClick={toggleShipment}
                title={shipmentOpen ? 'Hide Shipment columns' : 'Show Shipment columns'}
              >
                <i className={`fas fa-${shipmentOpen ? 'minus' : 'plus'}`}></i>
              </button>
            </th>
            {shipmentOpen && <th className="sp-th sp-th-group-header" colSpan={3}>Shipment</th>}
            <th className="sp-th sp-th-group sp-th-boxes-canship" rowSpan={2} title="includes WH stock and boxes to be produced by the ship date; excludes reserved boxes">
              Est. available<br/>boxes
              <button
                className={`sp-prod-toggle${prodPlanOpen ? '' : ' collapsed'}`}
                onClick={toggleProdPlan}
                title={prodPlanOpen ? 'Hide Production Plan' : 'Show Production Plan'}
              >
                <i className={`fas fa-${prodPlanOpen ? 'minus' : 'plus'}`}></i>
              </button>
            </th>
            {prodPlanOpen && (
              <th className="sp-th sp-th-group-header sp-th-prod" colSpan={prodDays.length}>
                Production Plan
              </th>
            )}
            <th className="sp-th sp-th-group-header sp-th-sales sp-block-left" colSpan={7}>Sales</th>
            <th className="sp-th sp-th-group-header sp-th-days sp-block-left" colSpan={3}>Days Supplied</th>
            <th className="sp-th sp-th-sep" rowSpan={2}></th>
            <th className="sp-th sp-th-group-header" colSpan={3}>
              Shipment Details
              <span className="sp-shipdetail-actions">
                <button
                  className="sp-shipdetail-btn sp-shipdetail-save"
                  onClick={onSaveBoxInputs}
                  disabled={boxSaveStatus === 'saving'}
                  title="Save planned boxes to database"
                >
                  <i className={`fas fa-${boxSaveStatus === 'saving' ? 'circle-notch sp-spin' : 'save'}`}></i>
                </button>
                {boxSaveStatus === 'saved' && <i className="fas fa-check sp-shipdetail-ok" title="Saved!"></i>}
                {boxSaveStatus === 'error'  && <i className="fas fa-times sp-shipdetail-err" title="Save error"></i>}
                <button
                  className="sp-shipdetail-btn sp-shipdetail-clear"
                  onClick={onClearBoxInputs}
                  title="Clear all planned box quantities"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </span>
            </th>
            <th className="sp-th sp-th-group sp-th-comments" rowSpan={2} title="Free-text comments per SKU">Comments</th>
          </tr>

          {/* Row 2: Sub-headers */}
          <tr className="sp-thead-sub">
            <th className="sp-th sp-th-sub sp-block-left">
              WFS
              <button
                className={`sp-prod-toggle${wfsOpen ? '' : ' collapsed'}`}
                onClick={toggleWfs}
                title={wfsOpen ? 'Hide Available / Inbound' : 'Show Available / Inbound'}
              >
                <i className={`fas fa-${wfsOpen ? 'minus' : 'plus'}`}></i>
              </button>
              {wfsUpdatedAt && <div className="sp-th-updated">{fmtTimestamp(wfsUpdatedAt)}</div>}
            </th>
            {wfsOpen && <>
              <th className="sp-th sp-th-sub" title="Available units (sum by SKU from the WFS file)">Available</th>
              <th className="sp-th sp-th-sub" title="Inbound units (sum by SKU from the WFS file)">Inbound</th>
            </>}
            <th className="sp-th sp-th-sub">WH</th>
            {shipmentOpen && <>
              <th className="sp-th sp-th-sub" title="Boxes Needed (sum by ASIN from Marketplace Shipments)">Needed<br/>box</th>
              <th className="sp-th sp-th-sub" title="Boxes Added (sum by ASIN from Marketplace Shipments)">Added<br/>box</th>
              <th className="sp-th sp-th-sub" title="Boxes Added × Bags per Box">Added<br/>pcs</th>
            </>}
            {prodPlanOpen && prodDays.map(d => (
              <th key={d} className="sp-th sp-th-sub sp-th-day">{fmtDateFull(d)}</th>
            ))}
            <th className="sp-th sp-th-sub sp-block-left">
              Avg Daily Sales (7d)
              {velUpdatedAt.ts7   && <div className="sp-th-updated">{fmtTimestamp(velUpdatedAt.ts7)}</div>}
              {velUpdatedAt.from7 && <div className="sp-th-updated">{fmtDateShort(velUpdatedAt.from7)} → {fmtDateShort(velUpdatedAt.to7)}</div>}
            </th>
            <th className="sp-th sp-th-sub">
              Avg Daily Sales (30d)
              {velUpdatedAt.ts30   && <div className="sp-th-updated">{fmtTimestamp(velUpdatedAt.ts30)}</div>}
              {velUpdatedAt.from30 && <div className="sp-th-updated">{fmtDateShort(velUpdatedAt.from30)} → {fmtDateShort(velUpdatedAt.to30)}</div>}
            </th>
            <th className="sp-th sp-th-sub">Trend (7 vs 30)</th>
            <th className="sp-th sp-th-sub">
              Actual Sales
              {actualSalesMeta.updatedAt && <div className="sp-th-updated">{fmtTimestamp(actualSalesMeta.updatedAt)}</div>}
              {actualSalesMeta.dateFrom  && <div className="sp-th-updated">{fmtDateShort(actualSalesMeta.dateFrom)} → {fmtDateShort(actualSalesMeta.dateTo)}</div>}
            </th>
            <th className="sp-th sp-th-sub" title="Monthly sales based on the average daily sales over the last 7 days">Est. monthly sales</th>
            <th className="sp-th sp-th-sub" title="Monthly forecast sales based on YoY data (Walmart tab)">Forecast Sales</th>
            <th className="sp-th sp-th-sub" title="Actual / Forecast">Act/Fct %</th>
            {[
              ['cur', 'Cur Stock', 'WFS Available / Velocity 7d', ' sp-block-left'],
              ['inbound', '+ Inbound', '(WFS Available + Inbound) / Velocity 7d', ''],
              ['ship', '+ Shipment', '(WFS + Units) / Velocity 7d', ''],
            ].map(([col, label, hint, extra]) => (
              <th key={col} className={`sp-th sp-th-sub sp-th-sortable${extra}`} title={`${hint} — click to sort`} onClick={() => handleDaysSort(col)}>
                {label}
                <i className={`fas fa-sort${daysSort.col === col ? (daysSort.dir === 'asc' ? '-up' : '-down') : ''} sp-sort-icon${daysSort.col === col ? ' active' : ''}`}></i>
                <ColumnFilterPopup
                  label={`${label} (days)`}
                  filter={daysFilters[col]}
                  onChange={f => setDaysFilters(prev => ({ ...prev, [col]: f }))}
                  onClear={() => setDaysFilters(prev => { const n = { ...prev }; delete n[col]; return n; })}
                />
              </th>
            ))}
            <th className="sp-th sp-th-sub">Bags/Box</th>
            <th className="sp-th sp-th-sub">Boxes</th>
            <th className="sp-th sp-th-sub">Units</th>
          </tr>
        </thead>

        <tbody>
          {grouped.length === 0 ? (
            <tr>
              <td className="sp-empty-state" colSpan={totalCols}>
                <i className="fas fa-box-open"></i>
                <span>No products found</span>
              </td>
            </tr>
          ) : (
            grouped.map(({ type, rows }) => (
              <React.Fragment key={type}>
                <tr className="sp-type-separator">
                  <td colSpan={totalCols} className="sp-type-label">
                    <div className="sp-type-label-inner">
                      <button
                        className={`sp-collapse-btn${collapsedTypes[type] ? ' collapsed' : ''}`}
                        onClick={() => toggleCollapse(type)}
                        title={collapsedTypes[type] ? 'Expand' : 'Collapse'}
                      >
                        <i className={`fas fa-${collapsedTypes[type] ? 'plus' : 'minus'}`}></i>
                      </button>
                      {TYPE_LABELS[type] || type}
                      <span className="sp-type-count">{rows.length}</span>
                    </div>
                  </td>
                </tr>

                {!collapsedTypes[type] && rows.map((product, rowIdx) => {
                  const sku = product.SKU;
                  const bagsPerBox = Number(product.bagsPerBox) || 0;
                  const stockWH = Number(product.stock_wh) || 0;
                  const stockWFS = Number(product.stock_wfs) || 0;
                  const wfsAvailable = Number(product.stock_wfs_available) || 0;
                  const wfsInbound   = Number(product.stock_wfs_inbound) || 0;
                  const shipNeededBox = Number(product.shipment_needed_box) || 0;
                  const shipAddedBox = Number(product.shipment_added_box) || 0;
                  const shipAddedPcs = shipAddedBox * bagsPerBox;
                  const willBeTaken = bagsPerBox > 0 ? shipAddedPcs - shipNeededBox * bagsPerBox : 0;
                  const prodSum = prodDays.reduce((sum, d) => sum + (planLookup[sku]?.[d] || 0), 0);
                  const producedSum = prodDays.reduce((sum, d) => sum + (producedData[sku]?.[d] || 0), 0);
                  const remainingProd = Math.max(0, prodSum - producedSum);
                  const boxesCanShip = bagsPerBox > 0
                    ? Math.floor((stockWH + willBeTaken + remainingProd) / bagsPerBox)
                    : 0;

                  const boxInput = boxInputs[sku] !== undefined ? boxInputs[sku] : '';
                  const units = bagsPerBox > 0 && boxInput !== '' ? bagsPerBox * Number(boxInput) : '';
                  const isOverLimit = boxInput !== '' && Number(boxInput) > 0 && Number(boxInput) > boxesCanShip;
                  const warningMsg = bagsPerBox === 0
                    ? 'Bags per Box not configured'
                    : `Exceeds est. available boxes (${boxesCanShip})`;

                  const { cur: curStockDays, inbound: plusInboundDays, ship: plusShipmentDays } = calcDays(product);

                  const willBeTakenNeg = willBeTaken < 0;

                  return (
                    <tr key={sku} className={`sp-row ${rowIdx % 2 === 0 ? 'sp-row-even' : 'sp-row-odd'}`}>
                      <td className="sp-td sp-td-sticky sp-td-sticky-1 sp-td-num">{rowIdx + 1}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-2 sp-td-name" title={product.name}>{product.name}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-3 sp-td-brand">{product.brandID || '—'}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-4 sp-td-asin">{product.GTIN || '—'}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-5 sp-td-sku">{sku}</td>

                      {/* OOS */}
                      <td className="sp-td sp-td-oos sp-block-left">
                        {product.GTIN
                          ? wfsAvailable === 0
                            ? <i className="fas fa-check sp-oos-check" title="Out of Stock on Walmart (no available units)"></i>
                            : '—'
                          : '—'}
                      </td>

                      {/* Stock */}
                      <td className="sp-td sp-td-stock">{stockWFS > 0 ? stockWFS : '—'}</td>
                      {wfsOpen && <>
                        <td className="sp-td sp-td-stock">{wfsAvailable > 0 ? wfsAvailable : '—'}</td>
                        <td className="sp-td sp-td-stock">{wfsInbound > 0 ? wfsInbound : '—'}</td>
                      </>}
                      <td className="sp-td sp-td-stock">{stockWH > 0 ? stockWH : '—'}</td>

                      {/* Reserved boxes */}
                      <td className={`sp-td sp-td-wh-ship${willBeTakenNeg ? ' sp-td-negative' : ''}`}>
                        {bagsPerBox > 0 ? willBeTaken : '—'}
                      </td>

                      {/* Shipment: Needed / Added / Added pcs */}
                      {shipmentOpen && <>
                        <td className="sp-td sp-td-ship-needed">{shipNeededBox > 0 ? shipNeededBox : '—'}</td>
                        <td className="sp-td sp-td-ship-added">{shipAddedBox > 0 ? shipAddedBox : '—'}</td>
                        <td className="sp-td sp-td-ship-pcs">{bagsPerBox > 0 && shipAddedBox > 0 ? shipAddedPcs : '—'}</td>
                      </>}

                      {/* Est. available boxes */}
                      <td className="sp-td sp-td-boxes-ship">
                        {bagsPerBox > 0 ? boxesCanShip : '—'}
                      </td>

                      {/* Production plan days */}
                      {prodPlanOpen && prodDays.map(d => {
                        const planned  = planLookup[sku]?.[d] || 0;
                        const produced = producedData[sku]?.[d] || 0;
                        const hasData  = planned > 0 || produced > 0;
                        return (
                          <td key={d} className={`sp-td sp-td-plan${planned > 0 ? ' sp-td-plan-has' : ''}`}>
                            {hasData ? (
                              <span className="sp-plan-produced">
                                <span className="sp-plan-planned">{planned || 0}</span>
                                <span className="sp-plan-divider">/</span>
                                <span className={`sp-plan-done${produced >= planned && planned > 0 ? ' sp-plan-done-full' : ''}`}>{produced}</span>
                              </span>
                            ) : '—'}
                          </td>
                        );
                      })}

                      {/* Sales block */}
                      <td className="sp-td sp-td-vel sp-block-left">
                        {velocityData[sku]?.vel_7d != null ? parseFloat(velocityData[sku].vel_7d).toFixed(1) : '—'}
                      </td>
                      <td className="sp-td sp-td-vel">
                        {velocityData[sku]?.vel_30d != null ? parseFloat(velocityData[sku].vel_30d).toFixed(1) : '—'}
                      </td>
                      {(() => {
                        const v7  = velocityData[sku]?.vel_7d;
                        const v30 = velocityData[sku]?.vel_30d;
                        if (v7 == null || v30 == null) return <td className="sp-td sp-td-trend">—</td>;
                        const n7 = parseFloat(v7), n30 = parseFloat(v30);
                        if (n7 > n30) return (
                          <td className="sp-td sp-td-trend sp-td-trend-up">
                            <i className="fas fa-caret-up sp-trend-up" title={`Vel 7d (${n7}) > Vel 30d (${n30})`}></i>
                          </td>
                        );
                        if (n7 < n30) return (
                          <td className="sp-td sp-td-trend sp-td-trend-down">
                            <i className="fas fa-caret-down sp-trend-down" title={`Vel 7d (${n7}) < Vel 30d (${n30})`}></i>
                          </td>
                        );
                        return <td className="sp-td sp-td-trend">—</td>;
                      })()}
                      <td className="sp-td sp-td-actual-sales">
                        {actualSalesData[sku]?.total_units != null ? actualSalesData[sku].total_units : '—'}
                      </td>
                      <td className="sp-td sp-td-run-rate">
                        {velocityData[sku]?.vel_7d != null
                          ? Math.round(parseFloat(velocityData[sku].vel_7d) * daysInCurrentMonth)
                          : '—'}
                      </td>
                      <td className="sp-td sp-td-forecast-sales">
                        {forecastSalesData[sku]?.total_units != null ? forecastSalesData[sku].total_units : '—'}
                      </td>
                      {(() => {
                        const actual   = actualSalesData[sku]?.total_units;
                        const forecast = forecastSalesData[sku]?.total_units;
                        if (actual == null || forecast == null || Number(forecast) === 0) {
                          return <td className="sp-td sp-td-actfct">—</td>;
                        }
                        const pct = Math.round(Number(actual) / Number(forecast) * 100);
                        return <td className="sp-td sp-td-actfct">{pct}%</td>;
                      })()}

                      {/* Days Supplied block */}
                      {[
                        [curStockDays, ' sp-block-left'],
                        [plusInboundDays, ''],
                        [plusShipmentDays, ''],
                      ].map(([val, extra], i) => {
                        if (val === null) return <td key={i} className={`sp-td sp-td-days${extra}`}>—</td>;
                        const days = Math.round(val);
                        const cls  = days < 30 ? ' sp-days-red' : days <= 60 ? ' sp-days-yellow' : '';
                        return <td key={i} className={`sp-td sp-td-days${extra}${cls}`}>{days}</td>;
                      })}

                      {/* Separator */}
                      <td className="sp-td sp-td-sep-cell"></td>

                      {/* Shipment Details */}
                      <td className="sp-td sp-td-bags">{bagsPerBox > 0 ? bagsPerBox : '—'}</td>

                      {/* Boxes input with over-limit warning */}
                      <td className="sp-td sp-td-boxes-input">
                        <div className="sp-box-input-wrapper">
                          <input
                            type="number"
                            className={`sp-box-input${isOverLimit ? ' sp-box-input-warning' : ''}`}
                            min="0"
                            value={boxInput}
                            placeholder="0"
                            onChange={e => handleBoxInput(sku, e.target.value)}
                          />
                          {isOverLimit && (
                            <span className="sp-box-warning-icon" title={warningMsg}>
                              <i className="fas fa-exclamation-triangle"></i>
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="sp-td sp-td-units">{units !== '' ? units : '—'}</td>

                      {/* Comments */}
                      <td className="sp-td sp-td-comment">
                        <InlineEdit
                          type="text"
                          value={commentsData[sku] || ''}
                          className="sp-inline-input sp-comment-input"
                          placeholder="…"
                          title={commentsData[sku] || 'Add comment'}
                          onSave={v => { onSaveComment && onSaveComment(sku, v); }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </tbody>

        {/* Total row — only shown when at least one box input is filled */}
        {(totalBoxes > 0 || totalUnits > 0) && (
          <tfoot>
            <tr className="sp-tfoot-total">
              <td colSpan={totalCols - 4} className="sp-tfoot-label">
                Total shipment
                <span className="sp-tfoot-sku-types" title="Number of unique SKUs in shipment">
                  &nbsp;·&nbsp;{totalSkuTypes} SKU{totalSkuTypes !== 1 ? 's' : ''}
                </span>
              </td>
              <td className="sp-tfoot-empty"></td>
              <td className="sp-tfoot-total-val">{totalBoxes}</td>
              <td className="sp-tfoot-total-val">{totalUnits}</td>
              <td className="sp-tfoot-empty"></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default WalmartShipmentsTable;
